import auth0 from 'auth0-js'
import { AUTH_CONFIG } from './auth0-variables'
import { api } from '../utils/index'

class Auth {
  accessToken
  idToken
  expiresAt
  userProfile
  userGroup
  tokenRenewalTimeout

  auth0 = new auth0.WebAuth({
    domain: AUTH_CONFIG.domain,
    clientID: AUTH_CONFIG.clientID,
    redirectUri: AUTH_CONFIG.callbackUrl,
    audience: AUTH_CONFIG.apiUrl,
    responseType: 'token id_token',
    scope: 'openid profile'
  })

  constructor() {
    this.login = this.login.bind(this)
    this.logout = this.logout.bind(this)
    this.handleAuthentication = this.handleAuthentication.bind(this)
    this.isAuthenticated = this.isAuthenticated.bind(this)
    this.getAccessToken = this.getAccessToken.bind(this)
    this.getIdToken = this.getIdToken.bind(this)
    this.renewSession = this.renewSession.bind(this)
    this.getProfile = this.getProfile.bind(this)
    this.getExpiryDate = this.getExpiryDate.bind(this)
    this.followRecoveryRedirect = this.followRecoveryRedirect.bind(this)
    this.restartLogin = this.restartLogin.bind(this)
    this.resetSession = this.resetSession.bind(this)
    this.bootstrapAppSession = this.bootstrapAppSession.bind(this)
    this.scheduleRenewal()
  }

  buildAuthError(code, description) {
    const error = new Error(description)
    error.error = code
    error.errorDescription = description
    return error
  }

  getAccessToken() {
    return this.accessToken
  }

  getIdToken() {
    return this.idToken
  }

  getAllowedServices() {
    return api.get('allowed_services/').then((response) => response.data.allowedServices)
  }

  getProfile() {
    return new Promise((resolve, reject) => {
      this.auth0.client.userInfo(this.accessToken, (err, profile) => {
        if (err) return reject(err)
        if (!profile) {
          return reject(this.buildAuthError('profile_unavailable', 'User profile is unavailable.'))
        }

        this.userProfile = profile
        api
          .get('groups/current/')
          .then((response) => {
            this.userGroup = response?.data?.name ? response.data.name : null
            resolve()
          })
          .catch((error) => {
            reject(error)
          })
      })
    })
  }

  getExpiryDate() {
    return JSON.stringify(new Date(this.expiresAt))
  }

  isAuthenticated() {
    // Check whether the current time is past the
    // access token's expiry time
    let expiresAt = this.expiresAt
    return new Date().getTime() < expiresAt
  }

  login(referrer) {
    this.auth0.authorize({
      redirectUri: `${AUTH_CONFIG.callbackUrl}?referrer=${referrer}`
    })
  }

  handleAuthentication() {
    return new Promise((resolve, reject) => {
      this.auth0.parseHash((err, authResult) => {
        if (err) return reject(err)
        if (!authResult || !authResult.idToken) {
          return reject(err)
        }

        this.setSession(authResult)
        resolve()
      })
    })
  }

  setCookie(c_name, value, exdate) {
    const c_value = value + ((exdate == null) ? "" : "; expires=" + exdate);
    document.cookie = c_name + "=" + c_value;
  }

  followRecoveryRedirect(redirectTo) {
    window.location.assign(redirectTo)
  }

  restartLogin(referrer = '') {
    const loginUrl = new URL('/login', window.location.origin)

    if (referrer) {
      loginUrl.searchParams.set('referrer', referrer)
    }

    this.logout(loginUrl.toString())
  }

  resetSession() {
    this.accessToken = null
    this.idToken = null
    this.expiresAt = 0
    this.userProfile = null
    this.userGroup = null

    clearTimeout(this.tokenRenewalTimeout)
    localStorage.removeItem('isLoggedIn')
  }

  setSession(authResult) {
    // Set isLoggedIn flag in localStorage
    localStorage.setItem('isLoggedIn', 'true')

    // Set the time that the access token will expire at
    let expiresAt = authResult.expiresIn * 1000 + new Date().getTime()
    this.accessToken = authResult.accessToken
    this.idToken = authResult.idToken
    this.expiresAt = expiresAt

    this.setCookie('ranking_auth', this.accessToken, this.expiresAt)

    // schedule a token renewal
    this.scheduleRenewal()
  }

  bootstrapAppSession() {
    const shouldRestoreSession =
      this.isAuthenticated() || localStorage.getItem('isLoggedIn') === 'true'

    if (!shouldRestoreSession) {
      return Promise.reject(this.buildAuthError('login_required', 'Login required'))
    }

    const ensureSession = this.isAuthenticated() ? Promise.resolve() : this.renewSession()
    return ensureSession
      .then(() => {
        if (this.userProfile && this.userGroup) {
          return
        }

        return this.getProfile()
      })
      .then(() => this.getAllowedServices())
      .then((allowedServices) => ({
        allowedServices,
        profile: this.userProfile,
        userGroup: this.userGroup,
      }))
  }

  renewSession() {
    return new Promise((resolve, reject) => {
      this.auth0.checkSession({}, (err, authResult) => {
        if (err) {
          this.resetSession()
          return reject(err)
        }
        if (!authResult || !authResult.idToken) {
          this.resetSession()
          return reject(this.buildAuthError('login_required', 'Login required'))
        }

        this.setSession(authResult)
        resolve()
      })
    })
  }

  logout(returnTo = window.location.origin) {
    this.resetSession()
    this.auth0.logout({
      returnTo
    })
  }

  scheduleRenewal() {
    let expiresAt = this.expiresAt
    const timeout = expiresAt - Date.now()
    if (timeout > 0) {
      this.tokenRenewalTimeout = setTimeout(() => {
        this.renewSession().then(() => {
          return
        })
      }, timeout)
    }
  }
}

const auth = new Auth()

export default auth
